import { useEffect, useState } from 'react';
import { loginUser, isLoggedIn } from '../../utils/login';
import { fetchAPI, getSubdomain } from '../../utils/utils';
import { can } from '../../utils/permissions';
import { getCastellerNameWithMote, getCastellerSearchText } from '../../utils/castellerName';
import OfficialWebsiteLink from './OfficialWebsiteLink';

const COLLA = getSubdomain();

function EyeIcon({ crossed }) {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
            <circle cx="12" cy="12" r="3" />
            {crossed ? <line x1="3" y1="3" x2="21" y2="21" /> : null}
        </svg>
    );
}

function UserSelect({ socket, setUserId, setUserName, allowMembers }) {
    const [userData, setUserData] = useState(null);
    const getName = data => getCastellerNameWithMote(data)
    const searchableName = data => getCastellerSearchText(data)
    const stringNormalize = str => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    useEffect(() => {
        fetchAPI('/castellersInfo', setUserData);
    }, []);

    const users = Object.values(userData || {})
        .filter(user => allowMembers || can(user, 'ACCESS_WEBAPP'))
        .map(({ id, nom, cognom, mote }) => ({ id, nom, cognom, mote }))

    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(true);

    const handleUserSelection = (user) => {
        setUserId(user.id);
        setUserName(getName(user));
        setSearchTerm(getName(user));
        setShowDropdown(false);
    };

    const clearSearchTerm = () => {
        setUserId(null);
        setUserName('');
        setSearchTerm('');
        setShowDropdown(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && filteredUsers.length > 0) {
            handleUserSelection(filteredUsers[0]);
        } else if (e.key === 'Backspace') {
            clearSearchTerm();
        }
    };

    const getFilteredUsers = (search) => {
        let tempSearchTerm = search;
        let tempFilteredUsers = [];
    
        while (tempSearchTerm.length > 0) {
            tempFilteredUsers = users.filter(user =>
                stringNormalize(searchableName(user)).toLowerCase().includes(tempSearchTerm.toLowerCase())
            );
    
            if (tempFilteredUsers.length > 0) {
                break;
            }
    
            tempSearchTerm = tempSearchTerm.slice(0, -1);
        }
    
        return tempFilteredUsers;
    };
    
    const filteredUsers = searchTerm ? getFilteredUsers(searchTerm) : users;
    
        return (
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <input
                    value={searchTerm}
                    onChange={e => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setShowDropdown(true)}
                    // onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    placeholder="Busca't a la llista de tècnica"
                    style={{
                        width: '80%',
                        padding: '12px 20px',
                        margin: '8px 0',
                        display: 'inline-block',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                        fontSize: '16px',
                    }}
                />
                {filteredUsers.length > 0 && showDropdown && (
                    <div
                        style={{
                            width: '80%',
                            maxHeight: '200px',
                            overflowY: 'scroll',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            fontSize: '16px',
                        }}
                    >
                        {filteredUsers.map(user => (
                            <div
                                key={user.id}
                                onClick={() => handleUserSelection(user)}
                                style={{
                                    padding: '12px 20px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #ccc',
                                    backgroundColor: 'white',
                                    fontSize: '16px',
                                }}
                            >
                                {getName(user)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
}

function Warning({ message }) {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '10px 0',
            }}
        >
            <div
                style={{
                    padding: '15px',
                    backgroundColor: 'rgba(255, 0, 0, 0.5)',
                    color: 'white',
                    borderRadius: 5,
                    width: '75%',
                }}
            >
                {message}
            </div>
        </div>
    );
}

function Loading() {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '10px 0',
            }}
        >
            <div
                style={{
                    padding: '15px',
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    color: 'white',
                    borderRadius: 5,
                    width: '75%',
                }}
            >
                Entrant...
            </div>
        </div>
    );
}

function LoginApp({ isLogged, setIsLogged, socket, allowMembers = false, ...props }) {
    const [userId, setUserId] = useState(null);
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [isButtonPressed, setIsButtonPressed] = useState(false);
    const [wrongCredentials, setWrongCredentials] = useState(false);
    const [loading, setLoading] = useState(false);

    const login = (user, password) => {
        if (!user || !password) return;
        setLoading(true);

        loginUser(user, password)
            .then(correctCredentials => {
                setLoading(false)

                if (correctCredentials) {
                    setIsLogged(true);
                    props.setUserId(user)
                } else {
                    setWrongCredentials(true)
                }
            })
            .catch(() => {
                setLoading(false)
                setIsLogged(false)
                setWrongCredentials(true)
            })
    }

    useEffect(() => {
        if (loading) setWrongCredentials(false)
    }, [loading])

    // A password typed for one account must not stay visible or submittable for another.
    useEffect(() => {
        setWrongCredentials(false)
        setPassword(null)
        setShowPassword(false)
    }, [userId])

    return (<>
        <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            textAlign: 'center',
            padding: '20px',
        }}>
            <img
                src={`/escuts/${COLLA}.png`}
                alt="Escut de la colla"
                style={{
                    borderRadius: 10,
                    width: '50%',
                    minWidth: 100,
                    minHeight: 100,
                }}
            />

            <p>{allowMembers ? 'Inicia sessió.' : "Has d'entrar com a tècnica o junta."}</p>
            <OfficialWebsiteLink />

            { wrongCredentials && <Warning message="La contrasenya és incorrecta." /> }
            { loading && <Loading /> }

            <UserSelect socket={socket} setUserId={setUserId} setUserName={setUserName} allowMembers={allowMembers} />

            {
                userId && (
                    <>
                        <div style={{ position: 'relative', width: '80%', margin: '8px auto' }}>
                            {/* «Nom Cognom (Mote)», the same username as the mobile app, so password managers share the login */}
                            <input
                                type="text"
                                name="username"
                                value={userName}
                                readOnly
                                autoComplete="username"
                                aria-hidden="true"
                                tabIndex={-1}
                                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                            />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                placeholder="Contrasenya"
                                autoComplete="current-password"
                                value={password ?? ''}
                                onChange={e => setPassword(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 48px 12px 20px',
                                    display: 'block',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    boxSizing: 'border-box',
                                    fontSize: '16px',
                                }}
                                onKeyUp={e => {
                                    if (e.key === 'Enter') {
                                        login(userId, password)
                                    }
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(value => !value)}
                                aria-label={showPassword ? 'Amaga la contrasenya' : 'Mostra la contrasenya'}
                                aria-pressed={showPassword}
                                title={showPassword ? 'Amaga la contrasenya' : 'Mostra la contrasenya'}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    width: '44px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0,
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#606060',
                                    cursor: 'pointer',
                                }}
                            >
                                <EyeIcon crossed={showPassword} />
                            </button>
                        </div>
                        <button
                            onClick={() => login(userId, password)}
                            onPointerDown={() => setIsButtonPressed(true)}
                            onPointerUp={() => setIsButtonPressed(false)}
                            onPointerLeave={() => setIsButtonPressed(false)}
                            style={{
                                width: '80%',
                                backgroundColor: isButtonPressed ? '#3B9C41' : '#4CAF50',
                                color: 'white',
                                padding: '14px 20px',
                                margin: '8px 0',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px',
                            }}
                        >
                            Entrar
                        </button>
                    </>
                )
            }
        </div>
    </>)
}

export default LoginApp;
